import { datadogLogs } from "@datadog/browser-logs";
import { Channel } from "@redotech/congo-enums/channel";
import { CompensationMethodType } from "@redotech/congo-enums/compensation-method-type";
import { ProvisionType } from "@redotech/congo-enums/provision-type";
import { CustomerVariantPreferences } from "@redotech/redo-agents-cards/returns-agents/exchange-agent/customer-variant-preferences-helpers";
import { ShipmentMethodName } from "@redotech/redo-return-core-common/draft-return/draft-return-enums";
import {
  ReturnableItem,
  ReturnProduct,
} from "@redotech/redo-return-core-common/draft-return/draft-return-items";
import { ExchangeGroup } from "@redotech/redo-return-core-common/exchange-group";
import { ExchangesSelection } from "@redotech/redo-return-core-common/exchange-settings";
import { ReturnTypeEnum } from "@redotech/redo-return-core-common/return-type-enum";
import {
  ProductData,
  ProductVariantData,
  VariantData,
} from "@redotech/redo-return-operations-common/customer-portal/product";
import {
  DraftRegistration,
  isClaimOnly,
  isMerchantCreated,
  RegistrationUpsellType,
} from "@redotech/redo-return-operations-common/draft-registration";
import {
  CompensationMethod,
  DraftReturn,
  ReturnTotalCalculationsOutput,
} from "@redotech/redo-return-operations-common/draft-return/draft-return";
import { merchantFlowShortcutsEnabled } from "@redotech/redo-return-operations-common/draft-return/merchant-claim-mode";
import type { PickupCost } from "@redotech/redo-return-operations-common/draft-return/pickup-cost";
import {
  hasRequiredClaimDocuments,
  shouldCollectPoliceReport,
  shouldCollectReturnLevelClaimDocuments,
} from "@redotech/redo-return-operations-common/draft-return/pp-plus-claim-documents";
import { PurchaseChannel } from "@redotech/redo-return-warranties-common/warranties";
import { assertNever } from "@redotech/util/type";
import { ShippingEstimateData } from "./contexts/shipping-estimate";
import { ItemReturnFlowStep } from "./flow/util";
import type { ReturnStepScreenType } from "./hooks/use-flow-return/use-flow-return";
import {
  getShippingMethodOptions,
  methodsToDisplay,
} from "./hooks/useShipmentMethod/get-shipping-method-options";
import { CustomerPortalAppSettings } from "./settings";
import {
  getCombinedReturnItemsGrouped,
  shouldAddEmailOptInUpsell,
  shouldAddEwUpsell,
} from "./util";
import {
  exchangeAgentHasCompensationAdjustment,
  isExchangeAgentBonusEligible,
} from "./utils/exchange-agent-compensation";

export interface MerchantFlowBulkItem {
  returnableItemId: string;
  previousPendingItemId?: string;
}

export class NavigatorWithLogger implements Navigator {
  constructor(private navigator: Navigator) {}

  push<T extends RouteName>(
    ...args: undefined extends StackParamList[T]
      ? [screen: T] | [screen: T, params: StackParamList[T]]
      : [screen: T, params: StackParamList[T]]
  ): void {
    datadogLogs.logger.info("screen_list.push", {
      screen: args[0],
      params: args[1],
    });
    this.navigator.push(...args);
  }
  replace<T extends RouteName>(
    ...args: undefined extends StackParamList[T]
      ? [screen: T] | [screen: T, params: StackParamList[T]]
      : [screen: T, params: StackParamList[T]]
  ): void {
    datadogLogs.logger.info("screen_list.replace", {
      screen: args[0],
      params: args[1],
    });
    this.navigator.replace(...args);
  }
  pop(count?: number): void {
    this.navigator.pop(count);
  }
  dispatch(action: {
    type: "RESET";
    payload: {
      index: number;
      routes: Omit<StackParamListType<RouteName>, "isModal">[];
    };
  }): void {
    const lastScreen = action.payload.routes[action.payload.routes.length - 1];
    datadogLogs.logger.info("screen_list.dispatch", {
      screen: lastScreen?.name,
      params: lastScreen?.params,
    });
    this.navigator.dispatch(action);
  }
  setOptions(options: any): void {
    //No logging necessary
    this.navigator.setOptions(options);
  }
  currentScreen(): StackParamListType<RouteName> | undefined {
    if (!this.navigator.currentScreen) {
      throw new Error("currentScreen not implemented");
    }
    return this.navigator.currentScreen();
  }
  getStack(): StackParamListType<RouteName>[] {
    if (!this.navigator.getStack) {
      throw new Error("getStack not implemented");
    }

    return this.navigator.getStack();
  }
  navigate(options: Omit<StackParamListType<RouteName>, "isModal">): void {
    datadogLogs.logger.info("screen_list.navigate", {
      screen: options.name,
      params: options.params,
    });
    this.navigator.navigate(options);
  }
}
export interface Navigator {
  /**
   * Navigate to a screen, will search for the screen in the stack and navigate back if it exists
   */
  navigate(options: Omit<StackParamListType<RouteName>, "isModal">): void;

  /**
   * Push a screen to the stack, can push multiple screens on top
   */
  push<T extends RouteName>(
    ...args: undefined extends StackParamList[T]
      ? [screen: T] | [screen: T, params: StackParamList[T]]
      : [screen: T, params: StackParamList[T]]
  ): void;

  /**
   * Replace the current screen with a new screen
   */
  replace<T extends RouteName>(
    ...args: undefined extends StackParamList[T]
      ? [screen: T] | [screen: T, params: StackParamList[T]]
      : [screen: T, params: StackParamList[T]]
  ): void;

  /**
   * Pop the current screen from the stack
   */
  pop(count?: number): void;

  dispatch(action: {
    type: "RESET";
    payload: {
      index: number;
      routes: Omit<StackParamListType<RouteName>, "isModal">[];
    };
  }): void;

  getStack?(): StackParamListType<RouteName>[];

  currentScreen?(): StackParamListType<RouteName> | undefined;

  setOptions(options: any): void;
}

export enum StepInReturn {
  SELECT = "select",
  ITEM_DETAILS = "item-details",
  CLAIM_DOCUMENTS = "claim-documents",
  CHOOSE_COMPENSATION_METHOD = "choose-compensation-method",
  SHOP_NOW = "shop-now",
  SHOP_ON_SITE = "shop-on-site",
  SHIPPING_ADDRESS = "shipping-address",
  CHOOSE_SHIPMENT_METHOD = "choose-shipment-method",
  NEW_ORDER_METHOD = "new-order-method",
  REVIEW_RETURN = "review-return",
  WEB_REVIEW_RETURN = "web-review-return",
  PACKAGE_PICKUP_UPSELL = "package-pickup-upsell",
  PACKAGE_PICKUP = "package-pickup",
}

export enum StepInRegistration {
  RETAIL_PRODUCT_SELECTION = "retail-product-selection",
  ONLINE_PRODUCT_SELECTION = "online-product-selection",
  ADD_DETAILS = "add-details",
  USER_INFO = "user-info",
  FLOW = "flow",
  UPSELL = "upsell",
  REVIEW = "review",
  PURCHASE_CHANNEL = "purchase-channel",
  START_CLAIM = "start-claim",
  ADDRESS = "address",
}

export enum ReturnAddressType {
  SHIPPING = "Shipping",
  NEW_ORDER = "New Order",
}
export type ParamListBase = Record<string, object | undefined>;
//Enforces params to be passed for each screen if needed
export type StackParamListType<T> = T extends RouteName
  ? { name: T; params: StackParamList[T]; isModal: boolean }
  : never;

export enum RouteName {
  LOGIN = "login",
  REGISTRATION_LOGIN = "registration-login",
  REGISTRATION_ADD_DETAILS = "registration-add-details",
  REGISTRATION_UPSELL = "registration-upsell",
  START_RETAIL_RETURN_TYPE = "start-retail-return-type",
  RETAIL_PRODUCT_SELECTION = "retail-product-selection",
  ONLINE_PRODUCT_SELECTION = "online-product-selection",
  VARIANT_SELECTION = "variant-selection",
  USER_INFO = "user-info",
  REVIEW_REGISTRATION_MODAL = "review-registration-modal",
  REVIEW_REGISTRATION = "review-registration",
  SELECT_ITEMS = "select-items",
  ITEM_DETAILS = "item-details",
  MODAL_STACK = "modal-stack",
  MANUAL_FLOW = "manual-flow",
  REGISTRATION_FLOW = "registration-flow",
  VARIANT_PICKER = "variant-picker",
  CLAIM_DOCUMENTS = "claim-documents",
  CHOOSE_COMPENSATION_METHOD = "choose-compensation-method",
  ADDRESS = "address",
  ADDRESS_MODAL = "address-modal",
  EDIT_ADDRESS = "edit-address",
  REVIEW = "review",
  SHIPMENT_METHOD = "shipment-method",
  RETURN_CONFIRMATION = "return-confirmation",
  SHOP_NOW = "shop-now",
  SHOP_ON_SITE = "shop-on-site",
  CART = "cart",
  PACKAGE_PICKUP_UPSELL = "package-pickup-upsell",
  PACKAGE_PICKUP = "package-pickup",
  NEW_ORDER_METHOD = "new-order-method",
  VIEW_RETURNS = "view-returns",
  NAVIGATE_OUT = "navigate-out",
  CANT_FIND_ITEM_MODAL = "cant-find-item-modal",
  CHANGE_RESET_POINT = "change-reset-point",
  PURCHASE_CHANNEL = "purchase-channel",
  MERCHANT_FLOW = "merchant-flow",
  //Flow Screens
  FLOW_INPUT = "flow-input",
  FLOW_MULTIPLE_CHOICE = "flow-multiple-choice",
  FLOW_AI_RETURN_REASONS = "flow-ai-return-reasons",
  FLOW_INFORMATION_STEP = "flow-information-step",
  FLOW_REJECT_STEP = "flow-reject-step",
  FLOW_BLOCK_STEP = "flow-block-step",
  EXCHANGE_RECOMMENDATION_FLOW = "exchange-recommendation-flow",
  QUANTITY_SELECTION = "quantity-selection",
}

export const isModal: Record<RouteName, boolean> = {
  [RouteName.LOGIN]: false,
  [RouteName.REGISTRATION_LOGIN]: false,
  [RouteName.RETAIL_PRODUCT_SELECTION]: false,
  [RouteName.ONLINE_PRODUCT_SELECTION]: false,
  [RouteName.REGISTRATION_ADD_DETAILS]: false,
  [RouteName.REGISTRATION_UPSELL]: true,
  [RouteName.VARIANT_SELECTION]: true,
  [RouteName.USER_INFO]: true,
  [RouteName.REVIEW_REGISTRATION_MODAL]: true,
  [RouteName.REVIEW_REGISTRATION]: false,
  [RouteName.SELECT_ITEMS]: false,
  [RouteName.ITEM_DETAILS]: false,
  [RouteName.MODAL_STACK]: true,
  [RouteName.MANUAL_FLOW]: true,
  [RouteName.REGISTRATION_FLOW]: true,
  [RouteName.VARIANT_PICKER]: true,
  [RouteName.CLAIM_DOCUMENTS]: false,
  [RouteName.CHOOSE_COMPENSATION_METHOD]: false,
  [RouteName.ADDRESS]: false,
  [RouteName.ADDRESS_MODAL]: true,
  [RouteName.EDIT_ADDRESS]: true,
  [RouteName.REVIEW]: false,
  [RouteName.SHIPMENT_METHOD]: false,
  [RouteName.RETURN_CONFIRMATION]: false,
  [RouteName.SHOP_NOW]: false,
  [RouteName.SHOP_ON_SITE]: false,
  [RouteName.CART]: true,
  [RouteName.PACKAGE_PICKUP_UPSELL]: true,
  [RouteName.PACKAGE_PICKUP]: true,
  [RouteName.NEW_ORDER_METHOD]: false,
  [RouteName.VIEW_RETURNS]: false,
  [RouteName.NAVIGATE_OUT]: false,
  [RouteName.CHANGE_RESET_POINT]: true,
  [RouteName.PURCHASE_CHANNEL]: false,
  [RouteName.START_RETAIL_RETURN_TYPE]: false,
  [RouteName.MERCHANT_FLOW]: true,
  [RouteName.FLOW_INPUT]: true,
  [RouteName.FLOW_MULTIPLE_CHOICE]: true,
  [RouteName.FLOW_AI_RETURN_REASONS]: true,
  [RouteName.FLOW_INFORMATION_STEP]: true,
  [RouteName.FLOW_REJECT_STEP]: true,
  [RouteName.FLOW_BLOCK_STEP]: true,
  [RouteName.EXCHANGE_RECOMMENDATION_FLOW]: true,
  [RouteName.CANT_FIND_ITEM_MODAL]: true,
  [RouteName.QUANTITY_SELECTION]: true,
};

export type VariantPickerExchangeType = "variant" | "product";

export interface VariantPickerSelection {
  product: ProductData;
  variant: VariantData;
  exchangeType: VariantPickerExchangeType;
}

export interface StackParamList {
  [RouteName.LOGIN]: { startReturn?: boolean };
  [RouteName.REGISTRATION_LOGIN]: { isClaimOnly?: boolean };
  [RouteName.RETAIL_PRODUCT_SELECTION]: undefined;
  [RouteName.ONLINE_PRODUCT_SELECTION]: undefined;
  [RouteName.REGISTRATION_ADD_DETAILS]: undefined;
  [RouteName.REGISTRATION_UPSELL]: { upsellSteps?: RegistrationUpsellType[] };
  [RouteName.VARIANT_SELECTION]: { product: ReturnProduct };
  [RouteName.USER_INFO]: undefined;
  [RouteName.REVIEW_REGISTRATION_MODAL]: undefined;
  [RouteName.REVIEW_REGISTRATION]: undefined;
  [RouteName.SELECT_ITEMS]: undefined;
  [RouteName.ITEM_DETAILS]: undefined;
  [RouteName.MODAL_STACK]: {
    itemReturnFlowSteps: readonly ItemReturnFlowStep[];
    itemId: string;
  };
  [RouteName.MANUAL_FLOW]: {
    itemReturnFlowSteps: readonly ItemReturnFlowStep[];
    itemId: string;
    previousPendingItemId?: string;
  };
  [RouteName.REGISTRATION_FLOW]: {
    itemReturnFlowSteps: readonly ItemReturnFlowStep[];
  };
  [RouteName.VARIANT_PICKER]: {
    product: ProductData;
    /** A size swap of the returned item, or a new product. */
    exchangeType: VariantPickerExchangeType;
    onSubmit: (selection: VariantPickerSelection) => void;
    returnableItem?: ReturnableItem;
    /** Pooled return value for catalog browsing, where no single item applies. */
    pooledReturnValue?: number;
    returnedProductVariantData?: VariantData;
    customerVariantPreferences: CustomerVariantPreferences;
    settings?: CustomerPortalAppSettings;
    exchangeGroup?: ExchangeGroup;
    recommendedOptions?: { optionName: string; optionValue: string }[];
    allowStoreFill?: boolean;
    overrideTexts?: {
      title?: string;
      subtitle?: string;
      previousButtonText?: string;
      continueButtonText?: string;
    };
  };
  [RouteName.CLAIM_DOCUMENTS]: undefined;
  [RouteName.CHOOSE_COMPENSATION_METHOD]: {
    initialFavoritedProducts?: ProductVariantData[];
  };
  [RouteName.ADDRESS]: undefined;
  [RouteName.ADDRESS_MODAL]: undefined;
  [RouteName.EDIT_ADDRESS]: { type: ReturnAddressType };
  [RouteName.REVIEW]: undefined;
  [RouteName.SHIPMENT_METHOD]: undefined;
  [RouteName.RETURN_CONFIRMATION]: {
    returnId: string;
    openCheckout: boolean;
    showBack?: boolean | undefined;
  };
  [RouteName.SHOP_NOW]: {
    initialFavoritedProducts?: ProductVariantData[];
    exchangeAgentShopNowProps?: {
      returnedItem: ReturnableItem;
      returnedProductVariantData: VariantData | undefined;
    };
    // used for determining if we are there to add an exchange item to the product or to shop for the return as a whole
    pendingItemContext?: { returnableItemId: string; pendingItemId: string };
  };
  [RouteName.SHOP_ON_SITE]: {
    selectedProduct?: ProductData;
    shouldNavigateToShopRightAway?: boolean;
  };
  [RouteName.CART]: { loadingPromise?: Promise<void> };

  [RouteName.PACKAGE_PICKUP_UPSELL]: { pickupCost: PickupCost };
  [RouteName.PACKAGE_PICKUP]: { pickupCost: PickupCost };
  [RouteName.NEW_ORDER_METHOD]: undefined;
  [RouteName.VIEW_RETURNS]: { returnIds: string[] };
  [RouteName.NAVIGATE_OUT]: { uri?: string; startAtBeginning?: boolean }; // If not defined, navigates to return portal review page
  [RouteName.CHANGE_RESET_POINT]: {
    onResetDraftReturn: (stepIndex: number) => Promise<void>;
  };
  [RouteName.PURCHASE_CHANNEL]: undefined;
  [RouteName.START_RETAIL_RETURN_TYPE]: undefined;
  [RouteName.MERCHANT_FLOW]: {
    product?: ProductData;
    returnableItemId: string;
    totalReturnableValue?: number;
    previousPendingItemId?: string;
    /** When set, the form applies to every listed item instead of `returnableItemId` alone. */
    bulkItems?: MerchantFlowBulkItem[];
  };
  [RouteName.FLOW_INPUT]: { stepIndex: number };
  [RouteName.FLOW_MULTIPLE_CHOICE]: { stepIndex: number };
  [RouteName.FLOW_AI_RETURN_REASONS]: {
    stepIndex: number;
    previousThreadId?: string;
    nextQuestion?: string;
  };
  [RouteName.FLOW_INFORMATION_STEP]: { stepIndex: number };
  [RouteName.FLOW_REJECT_STEP]: { stepIndex: number };
  [RouteName.FLOW_BLOCK_STEP]: { stepIndex: number };
  [RouteName.CANT_FIND_ITEM_MODAL]: undefined;
  [RouteName.QUANTITY_SELECTION]: { product: ReturnProduct };
  [RouteName.EXCHANGE_RECOMMENDATION_FLOW]: {
    stepIndex: number;
    returnStepScreenType: ReturnStepScreenType;
    recommendationOverride?: {
      product: ProductData;
      recommendedOptions?: { optionName: string; optionValue: string }[];
      rankedProducts?: {
        product: ProductData;
        recommendedOptions?: { optionName: string; optionValue: string }[];
      }[];
      rankedProductsTitle?: string;
    };
  };
}

export function getNextScreen<T extends StepInReturn | StepInRegistration>(
  step: T,
  currentStep?: T,
): StackParamListType<RouteName> {
  switch (step) {
    case StepInReturn.SELECT:
      return {
        name: RouteName.SELECT_ITEMS,
        params: undefined,
        isModal: isModal[RouteName.SELECT_ITEMS],
      };
    case StepInReturn.ITEM_DETAILS:
      return {
        name: RouteName.ITEM_DETAILS,
        params: undefined,
        isModal: isModal[RouteName.ITEM_DETAILS],
      };
    case StepInReturn.CLAIM_DOCUMENTS:
      return {
        name: RouteName.CLAIM_DOCUMENTS,
        params: undefined,
        isModal: isModal[RouteName.CLAIM_DOCUMENTS],
      };
    case StepInReturn.CHOOSE_COMPENSATION_METHOD:
      return {
        name: RouteName.CHOOSE_COMPENSATION_METHOD,
        params: {},
        isModal: isModal[RouteName.CHOOSE_COMPENSATION_METHOD],
      };
    case StepInReturn.SHOP_NOW:
      return {
        name: RouteName.SHOP_NOW,
        params: {},
        isModal: isModal[RouteName.SHOP_NOW],
      };
    case StepInReturn.SHOP_ON_SITE:
      return {
        name: RouteName.SHOP_ON_SITE,
        params: {
          selectedProduct: undefined,
          shouldNavigateToShopRightAway:
            currentStep === StepInReturn.CHOOSE_COMPENSATION_METHOD,
        },
        isModal: isModal[RouteName.SHOP_ON_SITE],
      };
    case StepInReturn.SHIPPING_ADDRESS:
      return {
        name: RouteName.ADDRESS,
        params: undefined,
        isModal: isModal[RouteName.ADDRESS],
      };
    case StepInReturn.CHOOSE_SHIPMENT_METHOD:
      return {
        name: RouteName.SHIPMENT_METHOD,
        params: undefined,
        isModal: isModal[RouteName.SHIPMENT_METHOD],
      };
    case StepInReturn.PACKAGE_PICKUP_UPSELL:
    case StepInReturn.PACKAGE_PICKUP:
      // Only pushed with a real pickupCost; never in getStepsInReturn's list.
      throw new Error(`Cannot rebuild the ${step} screen`);
    case StepInReturn.REVIEW_RETURN:
      return {
        name: RouteName.REVIEW,
        params: undefined,
        isModal: isModal[RouteName.REVIEW],
      };
    case StepInReturn.NEW_ORDER_METHOD:
      return {
        name: RouteName.NEW_ORDER_METHOD,
        params: undefined,
        isModal: isModal[RouteName.NEW_ORDER_METHOD],
      };
    case StepInReturn.WEB_REVIEW_RETURN:
      return {
        name: RouteName.NAVIGATE_OUT,
        params: { uri: undefined, startAtBeginning: undefined },
        isModal: isModal[RouteName.NAVIGATE_OUT],
      };
    case StepInRegistration.RETAIL_PRODUCT_SELECTION:
      return {
        name: RouteName.RETAIL_PRODUCT_SELECTION,
        params: undefined,
        isModal: isModal[RouteName.RETAIL_PRODUCT_SELECTION],
      };
    case StepInRegistration.ONLINE_PRODUCT_SELECTION:
      return {
        name: RouteName.ONLINE_PRODUCT_SELECTION,
        params: undefined,
        isModal: isModal[RouteName.ONLINE_PRODUCT_SELECTION],
      };
    case StepInRegistration.ADD_DETAILS:
      return {
        name: RouteName.REGISTRATION_ADD_DETAILS,
        params: undefined,
        isModal: isModal[RouteName.REGISTRATION_ADD_DETAILS],
      };
    case StepInRegistration.USER_INFO:
      return { name: RouteName.USER_INFO, params: undefined, isModal: false };
    case StepInRegistration.FLOW:
      return {
        name: RouteName.REGISTRATION_FLOW,
        params: { itemReturnFlowSteps: [] },
        isModal: isModal[RouteName.REGISTRATION_FLOW],
      };
    case StepInRegistration.UPSELL:
      return {
        name: RouteName.REGISTRATION_UPSELL,
        params: { upsellSteps: undefined },
        isModal: isModal[RouteName.REGISTRATION_UPSELL],
      };
    case StepInRegistration.REVIEW:
      return {
        name: RouteName.REVIEW_REGISTRATION_MODAL,
        params: undefined,
        isModal: isModal[RouteName.REVIEW_REGISTRATION_MODAL],
      };
    case StepInRegistration.PURCHASE_CHANNEL:
      return {
        name: RouteName.PURCHASE_CHANNEL,
        params: undefined,
        isModal: isModal[RouteName.PURCHASE_CHANNEL],
      };
    case StepInRegistration.START_CLAIM:
      return {
        name: RouteName.START_RETAIL_RETURN_TYPE,
        params: undefined,
        isModal: isModal[RouteName.START_RETAIL_RETURN_TYPE],
      };
    case StepInRegistration.ADDRESS:
      return {
        name: RouteName.ADDRESS_MODAL,
        params: undefined,
        isModal: false,
      };

    default:
      return assertNever(step);
  }
}

export function navigateFromStack<T extends StepInReturn | StepInRegistration>(
  steps: T[],
  currentStep: T | undefined,
  navigation: Navigator,
  fastForward = true, //If true, will skip to the last step in the stack
) {
  if (steps.length === 0) {
    console.error("navigateFromStack: No steps to navigate to");
    throw new Error("No steps to navigate to");
  }

  if (steps.length === 1) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const nextScreenParams = getNextScreen(steps[0]!, currentStep);
    navigation.replace(nextScreenParams.name, nextScreenParams.params);

    return;
  }

  const secondToLastStep = steps[steps.length - 2];
  if (secondToLastStep === currentStep) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    navigation.navigate(getNextScreen(steps.pop()!, currentStep));
    return;
  }

  if (!fastForward) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const currentStepIndex = steps.indexOf(currentStep!);
    if (currentStepIndex !== -1 && currentStepIndex + 1 < steps.length) {
      navigation.navigate(
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        getNextScreen(steps[currentStepIndex + 1]!, currentStep),
      );
      return;
    }
  }
  //If we reach here it means we need to push more than one screen to the stack
  navigation.dispatch({
    type: "RESET",
    payload: {
      index: steps.length - 1,
      routes: steps.map((step) => {
        return getNextScreen(step, currentStep);
      }),
    },
  });
}

export function safePop(navigation: Navigator, callback: () => void) {
  if (navigation.getStack && navigation.getStack().length > 1) {
    navigation.pop();
  } else {
    callback();
  }
}

export function shouldAllowAdvancingPastItemDetails(
  draftReturn?: DraftReturn,
  bulkReturnsEnabled = false,
) {
  if (!draftReturn) {
    return false;
  }

  /** Without bulk returns, there's an easy 1:1 correspondence between selected items and pending items */
  if (!bulkReturnsEnabled) {
    const selectedItems = draftReturn.returnableItems.filter(
      (item) => item.selected,
    );
    const pendingItemsLength = draftReturn.pendingReturnItems.filter(
      // To determine bonus eligibility, we sometimes create temporary pending items on the draft return
      // without actually saving them to see what potential compensation methods would look like before the user has made a decision.
      (pendingItem) => !pendingItem.metadata?.temporary,
    ).length;
    return pendingItemsLength >= selectedItems.length;
  }

  const combinedReturnItems = getCombinedReturnItemsGrouped(draftReturn);
  for (const item of combinedReturnItems) {
    const pendingItem = draftReturn.pendingReturnItems.find(
      (p) => p.returnableItemId === item.id && !p.metadata?.temporary,
    );
    if (!pendingItem) {
      return false;
    }
  }
  return true;
}

/**
 * Flow types that offer the multi-option Return step; withdrawal flows carry
 * it too but are auto-walked, so they stay excluded.
 */
const ADVANCED_EXCHANGE_RETURN_TYPES: readonly ReturnTypeEnum[] = [
  ReturnTypeEnum.RETURN,
  ReturnTypeEnum.CLAIM,
  ReturnTypeEnum.WARRANTY,
];

function shouldMerchantSeeCompensationMethodScreen({
  availableMethods,
  draftReturn,
  settings,
}: {
  availableMethods: CompensationMethod[];
  draftReturn: DraftReturn;
  settings: CustomerPortalAppSettings;
}) {
  //If you choose variant exchange for all items, advancedExchangeAvailable will be false
  if (
    availableMethods.some(
      (method) =>
        method.type === CompensationMethodType.Exchange &&
        method.advancedExchangeAvailable,
    )
  ) {
    return true;
  }

  return shouldCustomerSeeCompensationMethodScreen({
    availableMethods,
    draftReturn,
    settings,
  });
}

function shouldCustomerSeeCompensationMethodScreen({
  availableMethods,
  draftReturn,
  settings,
}: {
  availableMethods: CompensationMethod[];
  draftReturn: DraftReturn;
  settings: CustomerPortalAppSettings;
}) {
  if (availableMethods.length === 1) {
    return false;
  }

  if (availableMethods.length === 2) {
    const exchange = availableMethods.find(
      (method) => method.type === CompensationMethodType.Exchange,
    );
    if (exchange && !exchange.advancedExchangeAvailable) {
      return false;
    }

    if (
      isExchangeAgentBonusEligible(draftReturn) &&
      draftReturn.selectedMethod
    ) {
      const exchangeAgentHasAdjustments =
        exchangeAgentHasCompensationAdjustment(draftReturn);
      if (!exchangeAgentHasAdjustments) {
        return false;
      }
    }
  }

  return true;
}
/*
  Returns a list of steps that the user has completed, if you need just the latest step just pop the last element.
  The list is to allow rebuilding the navigation stack when deep linking.
*/
export async function getStepsInReturn(
  draftReturn: DraftReturn,
  settings: CustomerPortalAppSettings,
  getReturnCalculations: () => Promise<{
    calculations: ReturnTotalCalculationsOutput;
  }>,
  channel: Channel,
  shippingEstimateContext: ShippingEstimateData,
): Promise<StepInReturn[]> {
  const selectedItems = draftReturn.returnableItems.filter(
    (item) => item.selected,
  );

  const steps: StepInReturn[] = [StepInReturn.SELECT];

  if (selectedItems.length <= 0) {
    return steps;
  }

  const canAdvancePastItemDetails = shouldAllowAdvancingPastItemDetails(
    draftReturn,
    settings.bulkReturnsEnabled,
  );

  if (
    draftReturn.returnType !== ReturnTypeEnum.WITHDRAWAL ||
    !canAdvancePastItemDetails
  ) {
    steps.push(StepInReturn.ITEM_DETAILS);
  }

  if (!canAdvancePastItemDetails) {
    return steps;
  }

  if (shouldCollectReturnLevelClaimDocuments({ draftReturn })) {
    steps.push(StepInReturn.CLAIM_DOCUMENTS);
    const collectedClaimDocuments = hasRequiredClaimDocuments({
      idDocumentCount: draftReturn.claimIdDocuments?.length ?? 0,
      supportingDocumentCount:
        draftReturn.claimSupportingDocuments?.length ?? 0,
      policeReportRequired: shouldCollectPoliceReport(draftReturn),
    });
    if (!collectedClaimDocuments) {
      return steps;
    }
  }

  const availableMethods = draftReturn.compensationMethods;
  const compensationMethod = draftReturn.selectedMethod;

  const shouldSeeCompensationMethodScreen = merchantFlowShortcutsEnabled(
    draftReturn,
  )
    ? shouldMerchantSeeCompensationMethodScreen({
        availableMethods,
        draftReturn,
        settings,
      })
    : shouldCustomerSeeCompensationMethodScreen({
        availableMethods,
        draftReturn,
        settings,
      });

  if (shouldSeeCompensationMethodScreen) {
    steps.push(StepInReturn.CHOOSE_COMPENSATION_METHOD);
  }

  //If compensation method is not selected we should return steps.
  if (!compensationMethod) {
    return steps;
  }
  const foundMethod = draftReturn.compensationMethods.find(
    (t) => t.type === CompensationMethodType.Exchange,
  );
  if (
    compensationMethod === CompensationMethodType.Exchange &&
    !merchantFlowShortcutsEnabled(draftReturn)
  ) {
    //We should only show shop now page if there exist an item that wasn't a variant exchangeitem; (including items that don't ignore price differences which are treated as advanced exchanges)
    const nonExchangeItems = Object.entries(
      foundMethod?.itemValues || [],
    ).filter((t) => t[1].isVariantExchange === false);

    const priceDifferenceExchangeItems = draftReturn.pendingReturnItems
      .filter(
        (item) =>
          item.variantExchangeItem != null ||
          (item.advancedExchangeItems?.length ?? 0) > 0,
      )
      .map((t) => t.id);

    const filtered = nonExchangeItems.filter(
      (t) => !priceDifferenceExchangeItems.includes(t[0]),
    );

    //If filtered is greater than 0, then we know they chose exchange option
    if (
      filtered.length > 0 &&
      foundMethod?.advancedExchangeAvailable &&
      ADVANCED_EXCHANGE_RETURN_TYPES.includes(draftReturn.returnType)
    ) {
      switch (settings.exchanges.selection) {
        case ExchangesSelection.RETURN_APP:
          steps.push(StepInReturn.SHOP_NOW);
          break;
        case ExchangesSelection.STORE_WEBSITE:
          if (channel === Channel.Shopapp) {
            steps.push(StepInReturn.SHOP_NOW);
          } else {
            steps.push(StepInReturn.SHOP_ON_SITE);
          }
          break;
      }
      const advancedExchangeItemsLength =
        draftReturn.newOrderData?.items.length ||
        0 - priceDifferenceExchangeItems.length;

      if (advancedExchangeItemsLength <= 0) {
        return steps;
      }
    }
  }

  const { calculations } = await getReturnCalculations();

  if (!calculations) {
    console.error(
      "At this point a compensation method should have been selected",
    );
    return steps;
  }

  const {
    hasNonGreenReturnItems,
    showNewItems,
    totalShippingFee,
    exchangeShippingFee,
    exchangeLandedCost,
    chargesExchangeShippingViaStripe,
    skipPayment,
    labelDeducted,
  } = calculations.processed;
  // Stripe-billed exchange shipping and its landed cost are charged at submit
  // even when the label is deducted, so they always need a card.
  const owesStripeExchangeCharge =
    !!chargesExchangeShippingViaStripe &&
    exchangeShippingFee + (exchangeLandedCost ?? 0) > 0;

  const shipmentMethod = draftReturn.shipmentMethod;

  if (
    !merchantFlowShortcutsEnabled(draftReturn) &&
    (calculations?.processed?.hasNonGreenReturnItems ||
      calculations?.processed?.showNewItems ||
      // A shipping address is required to submit a return, so if it does not exist, we need to ask for it
      !draftReturn.customer.shippingAddress)
  ) {
    steps.push(StepInReturn.SHIPPING_ADDRESS);
  }
  //The only way to tell if shipping_address has been seen is to check if they have chosen a shipment method yet
  if (
    !shipmentMethod &&
    !merchantFlowShortcutsEnabled(draftReturn) &&
    (hasNonGreenReturnItems || showNewItems)
  ) {
    return steps;
  }
  if (
    (hasNonGreenReturnItems || showNewItems) &&
    !(await skipShipmentMethodPage(
      draftReturn,
      shippingEstimateContext,
      settings,
    ))
  ) {
    steps.push(StepInReturn.CHOOSE_SHIPMENT_METHOD);
    if (!shipmentMethod) {
      return steps;
    }
  }

  const exchanges = (
    draftReturn.returnType === ReturnTypeEnum.RETURN
      ? settings.automation?.returnProcessing
      : settings.automation?.claimProcessing
  )?.exchanges;

  if (channel === Channel.Shopapp && settings.exchanges.instantExchangeOnly) {
    steps.push(StepInReturn.WEB_REVIEW_RETURN);
    return steps;
  }
  //Provision Selection (instant exchange or processed exchange)
  if (
    channel === Channel.Shopapp &&
    settings.exchanges.allowInstant &&
    exchanges !== "open" &&
    hasNonGreenReturnItems &&
    draftReturn.returnType !== ReturnTypeEnum.CLAIM &&
    draftReturn.returnType !== ReturnTypeEnum.WARRANTY &&
    draftReturn.returnType !== ReturnTypeEnum.RECYCLE &&
    ((draftReturn?.newOrderData?.items || [])?.length > 0 ||
      Object.values(foundMethod?.itemValues || []).some(
        (item) => item.isVariantExchange,
      ))
  ) {
    steps.push(StepInReturn.NEW_ORDER_METHOD);

    if (!draftReturn.provisionType) {
      return steps;
    }
  }

  //If there is any shipping fee (and no skip payment/ label deducted), we must push to Web Review Return
  if (
    channel === Channel.Shopapp &&
    (draftReturn.provisionType === ProvisionType.Instant ||
      ((totalShippingFee > 0 || (exchangeLandedCost ?? 0) > 0) &&
        !skipPayment &&
        !labelDeducted) ||
      owesStripeExchangeCharge)
  ) {
    steps.push(StepInReturn.WEB_REVIEW_RETURN);
    return steps;
  }

  steps.push(StepInReturn.REVIEW_RETURN);
  return steps;
}

/**
 * This is the same as getStepsInReturn but for the registration flow
 */
export function getStepsInRegistration(
  draftRegistration: DraftRegistration,
  settings: CustomerPortalAppSettings,
): StepInRegistration[] {
  const steps: StepInRegistration[] = [];
  const { purchaseChannel, channelName } = draftRegistration.source;
  const numberOfProducts = draftRegistration.products.length;

  steps.push(StepInRegistration.PURCHASE_CHANNEL);
  if (!channelName) {
    return steps;
  }

  // Collect address before product selection so we can fetch the catalog
  // (and persist currency) in the customer's Shopify market. Always shown —
  // the form pre-populates from `userAddress` so edits are still possible.
  // Gate on `addressConfirmed` rather than address presence so movers can
  // update a stale address pre-populated from their customer profile or
  // Shopify shipping address.
  steps.push(StepInRegistration.ADDRESS);
  if (!draftRegistration.addressConfirmed) {
    return steps;
  }

  // Contact info is collected once per registration, before product selection,
  // so changing the selected product doesn't ask for it again.
  steps.push(StepInRegistration.USER_INFO);
  if (!draftRegistration.userInfoConfirmed) {
    return steps;
  }

  switch (purchaseChannel) {
    case PurchaseChannel.ONLINE:
      if (numberOfProducts === 0) {
        steps.push(StepInRegistration.ONLINE_PRODUCT_SELECTION);
      } else if (
        numberOfProducts === 1 &&
        !draftRegistration.productVariantResponses
      ) {
        // Open the registration flow over the details screen so a single
        // product goes straight to the flow, while back still lands on it.
        steps.push(StepInRegistration.ADD_DETAILS, StepInRegistration.FLOW);
      } else {
        steps.push(StepInRegistration.ADD_DETAILS);
      }
      break;
    case PurchaseChannel.RETAIL:
      steps.push(StepInRegistration.RETAIL_PRODUCT_SELECTION);
      break;
    default:
      assertNever(purchaseChannel);
  }

  if (!draftRegistration.productVariantResponses) {
    return steps;
  }

  const shouldShowEmailUpsell = shouldAddEmailOptInUpsell(
    draftRegistration,
    settings,
  );
  const shouldShowEWUpsell = shouldAddEwUpsell(draftRegistration, settings);
  const isClaimOnly_ = isClaimOnly(draftRegistration);
  const isMerchantCreated_ = isMerchantCreated(draftRegistration);
  const shouldAddUpsellStep =
    (shouldShowEmailUpsell || shouldShowEWUpsell) &&
    !isClaimOnly_ &&
    !isMerchantCreated_;
  if (shouldAddUpsellStep) {
    steps.push(StepInRegistration.UPSELL);
    return steps;
  }

  if (isClaimOnly_) {
    steps.push(StepInRegistration.START_CLAIM);
    return steps;
  }

  steps.push(StepInRegistration.REVIEW);

  return steps;
}

const skipShipmentMethodPage = async (
  draftReturn: DraftReturn,
  shippingEstimateContext: ShippingEstimateData,
  settings: CustomerPortalAppSettings,
) => {
  const shipmentMethodOptions = await getShippingMethodOptions(
    draftReturn,
    shippingEstimateContext,
    settings,
  );
  const displayableOptions = shipmentMethodOptions.filter((method) =>
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    methodsToDisplay.includes(method.type as any),
  );
  if (
    merchantFlowShortcutsEnabled(draftReturn) &&
    displayableOptions.length === 1 &&
    displayableOptions[0]?.type === ShipmentMethodName.MANUAL_LABEL
  ) {
    return false;
  }
  return displayableOptions.length <= 1;
};
